import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Loader2 } from "lucide-react";
import { signInWithGoogle, sendMagicLink } from "@/lib/firebase";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";

export default function AuthDialog({ open, onOpenChange }) {
  const { exchangeIdToken } = useAuth();
  const nav = useNavigate();
  const [loadingG, setLoadingG] = useState(false);
  const [email, setEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [loadingM, setLoadingM] = useState(false);

  const handleGoogle = async () => {
    setLoadingG(true);
    try {
      const idToken = await signInWithGoogle();
      await exchangeIdToken(idToken);
      toast.success("Signed in with Google");
      onOpenChange(false);
      nav("/dashboard");
    } catch (e) {
      console.error(e);
      toast.error(e?.code === "auth/popup-closed-by-user" ? "Sign-in cancelled" : (e?.message || "Google sign-in failed"));
    } finally {
      setLoadingG(false);
    }
  };

  const handleMagic = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoadingM(true);
    try {
      await sendMagicLink(email);
      setMagicSent(true);
      toast.success("Magic link sent — check your inbox");
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to send magic link");
    } finally {
      setLoadingM(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border border-border sm:max-w-md p-0" data-testid="auth-dialog">
        <div className="p-8 border-b border-border">
          <div className="eyebrow text-muted-foreground mb-3">Sign in</div>
          <DialogHeader className="p-0 space-y-2 text-left">
            <DialogTitle className="font-heading text-2xl font-light tracking-tight">
              Continue to Splink
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              One account across SocialPulse, FoodPulse, IndianFamilyTree, NaturePulse & Splink.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-8 space-y-4">
          <button
            data-testid="auth-google-btn"
            onClick={handleGoogle}
            disabled={loadingG}
            className="w-full flex items-center justify-center gap-3 py-3 border border-foreground bg-foreground text-background hover:opacity-90 transition-opacity text-sm font-medium disabled:opacity-50"
          >
            {loadingG ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden>
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            Continue with Google
          </button>

          <div className="flex items-center gap-3 text-xs text-muted-foreground uppercase tracking-widest">
            <div className="flex-1 h-px bg-border" />
            or
            <div className="flex-1 h-px bg-border" />
          </div>

          {!magicSent ? (
            <form onSubmit={handleMagic} className="space-y-3">
              <label className="eyebrow text-muted-foreground">Email magic link</label>
              <Input
                data-testid="auth-email-input"
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none border border-border h-11 font-mono text-sm"
              />
              <Button
                data-testid="auth-magic-btn"
                type="submit"
                disabled={loadingM || !email}
                variant="outline"
                className="w-full rounded-none border-foreground h-11"
              >
                {loadingM ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send magic link
              </Button>
            </form>
          ) : (
            <div className="border border-border p-4 text-sm">
              <div className="font-mono text-xs mb-1">{email}</div>
              <p className="text-muted-foreground">Check your inbox for a sign-in link. Click it to complete sign-in.</p>
            </div>
          )}

          <p className="text-xs text-muted-foreground pt-2">
            By continuing you agree to the Terms & Privacy Policy.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
